import { getCalendarDateStringInTimeZone, intlDateFormatter } from 'lib/datetime/dateUtils'

export function isAirportRelatedTransfer(item: {
  type: App.ExperienceTransferType
  airport?: { code?: string }
  pickupPoint?: { iataCode?: string }
  dropoffPoint?: { iataCode?: string }
}): boolean {
  return !!(item.pickupPoint?.iataCode?.trim() || item.dropoffPoint?.iataCode?.trim() ||
    (/^[A-Z]{3}$/.test(item.airport?.code ?? '') && ['AIRPORT-TO-HOTEL', 'HOTEL-TO-AIRPORT'].includes(item.type)))
}

/** A local pickup date must never be interpreted as midnight in the viewer's zone. */
export function isTransferzFlightNumberEditable(
  item: App.OrderTransferItem,
  pickupTimeZone?: string,
  now = new Date(),
): boolean {
  if (item.provider !== 'tfz' || item.status !== 'completed' || !isAirportRelatedTransfer(item)) return false

  // Transferz pickup_date can include the local pickup time without an offset.
  const day = (item.pickupDate || item.date).split('T')[0]
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return false

  const timeZone = pickupTimeZone?.trim()
  // Without a pickup zone, only a date future everywhere is known to be future.
  const latestToday = getCalendarDateStringInTimeZone(now, 'Pacific/Kiritimati')
  if (!timeZone) return day > latestToday

  try {
    // Machine comparison: read zoned wall time without reparsing it in the viewer's zone.
    const localTime = intlDateFormatter(now, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }, 'en-GB', timeZone)
    const today = getCalendarDateStringInTimeZone(now, timeZone)
    if (day !== today) return day > today
    const time = item.pickupTime || item.time
    if (!/^\d{2}:\d{2}(?::\d{2})?$/.test(time)) return true
    return time.slice(0, 5) > localTime
  } catch(error) {
    if (!(error instanceof RangeError)) throw error
    return day > latestToday
  }
}

export function isTransferzFlightNumberMissing(item: App.OrderTransferItem, pickupTimeZone?: string, now?: Date): boolean {
  return isTransferzFlightNumberEditable(item, pickupTimeZone, now) && !item.flightNumber?.trim()
}

export function getTransferzFlightNumberTarget(items: Array<App.OrderTransferItem>, bookingDetails?: Record<string, App.ExperienceBookingDetails>): string {
  const item = items.find(transfer => isTransferzFlightNumberMissing(transfer, bookingDetails?.[transfer.id]?.providerData?.pickupTimeZone)) ??
    items.find(transfer => isTransferzFlightNumberEditable(transfer, bookingDetails?.[transfer.id]?.providerData?.pickupTimeZone))
  return item ? `transfer-flight-number-${item.id}` : items.some(transfer => transfer.provider === 'tfz') ? 'order-airport-transfers' : 'order-transfers'
}
